'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { usePersistedState } from '@/lib/usePersistedState';
import { useChatHistory } from '@/lib/useChatHistory';
import { useActiveProject } from '@/lib/useActiveProject';
import { isElectron, gateway, chat as chatApi } from '@/lib/api';
import { executeDoTokens } from '@/future/voice/commands';
import type { ChatMessage, ChatImage, ToolCall, StreamEvent, TokenUsage, MessagePart } from './types';

// Always go through same-origin proxy so the chat works through cloudflared/tunnels on mobile.
const GATEWAY_URL = typeof window === 'undefined'
  ? (process.env.NEXT_PUBLIC_GATEWAY_URL || 'http://localhost:18790')
  : '/api/gw';

export type ConnectionStatus = 'connected' | 'disconnected' | 'checking';

/**
 * Direct id-targeted merge-write to the disk history (2026-07-08 scar: Boss
 * watched finished replies VANISH). useChatHistory's debounced setMessages is
 * correctly suppressed after a project switch (hydratedSlugRef guard), but that
 * also silently dropped the LEGITIMATE final commit of a run that finished
 * after the switch — the assistant placeholder stayed '' on disk forever and
 * every rehydrate replaced the live bubble with nothing. This helper bypasses
 * the guard safely: it merges by message id into the file for the slug the
 * run STARTED on, never blind-overwrites other writers.
 */
async function persistMessageDirect(slug: string, msg: ChatMessage) {
  try {
    const res: any = await gateway.chatHistory.read(slug);
    const disk: ChatMessage[] = Array.isArray(res?.messages) ? res.messages : [];
    const i = disk.findIndex(m => m?.id === msg.id);
    if (i >= 0) disk[i] = { ...disk[i], ...msg };
    else {
      disk.push(msg);
      disk.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
    }
    await gateway.chatHistory.write(slug, disk);
  } catch { /* disk unreachable — in-memory state is still committed */ }
}

/** Remove an abandoned empty placeholder from disk so it can't ghost-render. */
/** Visible TEXT of a message — text parts only, mirroring entryText() in
 *  shared/forget-message.cjs so the UI message can be matched to its entry in
 *  the SDK context tree. Thinking/tool blocks are excluded on both sides. */
function textPartsOf(m: ChatMessage): string {
  if (m.parts && m.parts.length) {
    return m.parts.filter(p => p.type === 'text').map(p => (p as { content: string }).content).join('');
  }
  return m.content || '';
}

async function removeMessageDirect(slug: string, msgId: string) {
  try {
    const res: any = await gateway.chatHistory.read(slug);
    const disk: ChatMessage[] = Array.isArray(res?.messages) ? res.messages : [];
    const next = disk.filter(m => m?.id !== msgId);
    if (next.length !== disk.length) await gateway.chatHistory.write(slug, next);
  } catch { /* ignore */ }
}

export function useChat() {
  const [activeProject] = useActiveProject();
  const slug = activeProject?.slug ?? 'general';
  const usageKey = activeProject ? `chat:usage:${activeProject.slug}` : 'chat:usage:general';
  // Disk-backed in Electron (~/Library/Application Support/beast/chat-history/<slug>.json),
  // localStorage in browser. Lazy useState init reads localStorage synchronously
  // on first render so saved messages display immediately, then disk content
  // (Electron source-of-truth) replaces it once the IPC round-trip resolves.
  const [messages, setMessages, historyHydrated] = useChatHistory<ChatMessage>(slug);
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState('');
  const [streamTools, setStreamTools] = useState<ToolCall[]>([]);
  const [streamParts, setStreamParts] = useState<MessagePart[]>([]);
  const [thinking, setThinking] = useState(false);
  const [usage, setUsage] = usePersistedState<TokenUsage | null>(usageKey, null);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('checking');
  const [lastFailedMessage, setLastFailedMessage] = useState<string | null>(null);

  // Ref to always hold the latest setMessages — prevents stale closure when
  // chatKey changes mid-stream (activeProject hydrates during streaming)
  const setMessagesRef = useRef(setMessages);
  setMessagesRef.current = setMessages;
  // Latest messages snapshot for imperative reads (e.g. delete needs the
  // pre-removal index for undo) without adding `messages` to callback deps.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const abortRef = useRef<AbortController | null>(null);
  const streamTextRef = useRef('');
  const streamToolsRef = useRef<ToolCall[]>([]);
  const streamPartsRef = useRef<MessagePart[]>([]);
  const streamFlushRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const streamingRef = useRef(false);
  // ID of the "building" assistant message — used to update it in-place in messages array
  const streamMsgIdRef = useRef<string | null>(null);
  const autoSaveRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Active IPC event listener cleanup — prevents leaked listeners from doubling events
  const ipcUnsubRef = useRef<(() => void) | null>(null);
  // Message queue — messages sent during streaming are queued and processed after
  const messageQueueRef = useRef<Array<{ text: string; context?: string; images?: ChatImage[] }>>([]);

  // VOICE BRIDGE (Boss: voice must look EXACTLY like typed chat — thought
  // process, tool cards, live streaming). BeastMode dispatches its transcript
  // here; we run it through the SAME send() as typed messages, so rendering
  // is identical by construction. Text deltas are re-broadcast for TTS.
  const voiceTurnRef = useRef(false);

  // Live merge-poll: messages can be appended to the history file from
  // OUTSIDE this page — voice-conversation mirror, voice-confirmed delegated
  // tasks and their work log (Boss must SEE delegated work in the Beast
  // chat). History only hydrated on mount before this; poll every 4s while
  // idle and merge any message ids we don't have yet.
  useEffect(() => {
    if (!historyHydrated) return;
    const iv = setInterval(async () => {
      if (streamingRef.current || document.hidden) return;
      try {
        const res: any = await gateway.chatHistory.read(slug);
        const disk: ChatMessage[] = Array.isArray(res?.messages) ? res.messages : [];
        if (!disk.length) return;
        setMessagesRef.current(prev => {
          const have = new Set(prev.map(m => m.id));
          const fresh = disk.filter(m => m?.id && !have.has(m.id));
          // live work-log: voicetask_* messages are UPDATED in place on disk
          // while the delegated task runs — take the disk version when it changed
          const diskById = new Map(disk.map(m => [m.id, m]));
          let updated = false;
          const next = prev.map(m => {
            if (!m.id?.startsWith('voicetask_')) return m;
            const d = diskById.get(m.id);
            if (d && ((d as any).content !== (m as any).content
              || JSON.stringify((d as any).parts || null) !== JSON.stringify((m as any).parts || null))) { updated = true; return d; }
            return m;
          });
          if (!fresh.length && !updated) return prev;
          // NO full re-sort (Boss: "stop refreshing the chat") — reordering
          // the whole list on every poll made the view jump. Keep existing
          // order, append new external messages at the end.
          return [...next, ...fresh];
        });
      } catch { /* gateway briefly down — next tick */ }
    }, 4000);
    return () => clearInterval(iv);
  }, [slug, historyHydrated]);

  // Health check
  useEffect(() => {
    const check = () => {
      gateway.health()
        .then((data: any) => setConnectionStatus(data?.status === 'ok' || data?.status === 'booting' ? 'connected' : 'disconnected'))
        .catch(() => setConnectionStatus('disconnected'));
    };
    check();
    const interval = setInterval(check, 30_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setLoaded(true);
  }, []);

  // Clean up stale "running" tools from interrupted streams
  // If we're not streaming but saved messages have tools stuck in "running",
  // mark them as errored so they don't show a spinner forever.
  // Gated on historyHydrated: in Electron the messages arrive from disk
  // ASYNCHRONOUSLY — running this on bare mount saw an empty array and
  // never cleaned anything.
  useEffect(() => {
    if (!historyHydrated || streamingRef.current) return;
    setMessages(prev => {
      let changed = false;
      const fixed = prev.map(m => {
        if (m.role !== 'assistant' || !m.tools && !m.parts) return m;
        const fixTools = m.tools?.map(t => {
          if (t.status === 'running') { changed = true; return { ...t, status: 'error' as const, output: t.output || '[interrupted]' }; }
          return t;
        });
        const fixParts = m.parts?.map(p => {
          if (p.type === 'tool' && p.tool.status === 'running') { changed = true; return { ...p, tool: { ...p.tool, status: 'error' as const, output: p.tool.output || '[interrupted]' } }; }
          return p;
        });
        return { ...m, tools: fixTools, parts: fixParts };
      });
      return changed ? fixed : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyHydrated]);

  // Reconnect after refresh — two cases:
  //  1. Gateway is STILL WORKING the previous turn (isStreaming) → surface the
  //     "working…" state and poll /session until it goes idle, then take the
  //     finished reply from /chat/last. Without this, a mid-run reload showed
  //     the chat as idle forever while the agent kept working server-side.
  //  2. Gateway already finished while we were away → one-shot /chat/last
  //     restore of the completed response (original behavior).
  //
  // CRITICAL: Only runs ONCE on initial mount (after hydration).
  // Must NOT re-run after streaming completes — that causes it to fetch
  // the PREVIOUS response from /chat/last and overwrite the current one.
  // The hasSentRef guard ensures reconnect never fires after send() runs.
  const hasSentRef = useRef(false);
  const reconnectRanRef = useRef(false);
  useEffect(() => {
    // Wait for BOTH flags: `loaded` flips on bare mount, but in Electron the
    // messages arrive from disk ASYNCHRONOUSLY (localStorage mirror is removed
    // after disk migration) — gating on `loaded` alone ran this against an
    // EMPTY message list and the reattach never fired after a reload.
    if (!loaded || !historyHydrated) return;
    if (reconnectRanRef.current) return; // once per page load
    // If send() was already called in this page session, don't reconnect.
    // The finally block in send() handles commitment. Reconnect is ONLY
    // for recovering from a page refresh that interrupted a previous stream.
    if (hasSentRef.current) return;
    reconnectRanRef.current = true;

    const lastMsg = messages[messages.length - 1];
    if (!lastMsg) return;

    // Only attempt reconnect if last message suggests an interrupted stream:
    // - User message with no assistant response following it
    // - Empty/partial assistant message (from auto-save)
    const lastAssistant = messages.filter(m => m.role === 'assistant').pop();
    const shouldCheck = lastMsg.role === 'user' ||
      (lastMsg.role === 'assistant' && (!lastMsg.content || lastMsg.content.length < 50));

    let cancelled = false;
    (async () => {
      // Is the gateway still working the turn this page lost on reload?
      let working = false;
      try { working = !!((await gateway.session()) as any)?.isStreaming; } catch { /* gateway down → case 2 */ }
      if (cancelled || hasSentRef.current) return;

      if (working) {
        // ── case 1: reattach to the live run ──
        // Reuse the trailing assistant placeholder if there is one, seeding the
        // live bubble with EVERYTHING auto-save captured — text AND tool blocks.
        // (StreamingBubble replaces the placeholder in the list while streaming;
        // seeding only text made the saved tool blocks vanish, which read as
        // "the chat stopped".) Otherwise append a fresh placeholder.
        let msgId: string;
        if (lastMsg.role === 'assistant') {
          msgId = lastMsg.id;
          streamTextRef.current = lastMsg.content || '';
          streamToolsRef.current = lastMsg.tools ? [...lastMsg.tools] : [];
          // Auto-save persists content + tools but NOT parts — synthesize the
          // tool parts so the bubble keeps showing them (tool_end events match
          // these seeded blocks by id and fill their outputs in live).
          streamPartsRef.current = lastMsg.parts && lastMsg.parts.length > 0
            ? [...lastMsg.parts]
            : streamToolsRef.current.map(t => ({ type: 'tool' as const, tool: t }));
          setStreamText(streamTextRef.current);
          setStreamTools(streamToolsRef.current);
          setStreamParts([...streamPartsRef.current]);
        } else {
          msgId = `msg_reattach_${Date.now()}`;
          setMessagesRef.current(prev => [...prev, { id: msgId, role: 'assistant' as const, content: '', timestamp: Date.now() }]);
        }
        streamMsgIdRef.current = msgId;
        setStreaming(true);
        streamingRef.current = true;
        setThinking(true);
        console.log('[chat] Reattached — agent still working the previous turn');

        // Electron: the main process keeps broadcasting chat:event through the
        // reload — resubscribe so tools/text stream LIVE again. (Browser has no
        // reattach stream for the main session; it polls and takes /chat/last.)
        let sawDone = false;
        let gotLive = false;
        let reattachUnsub: (() => void) | null = null;
        if (isElectron) {
          if (ipcUnsubRef.current) ipcUnsubRef.current();
          reattachUnsub = chatApi.onChatEvent((event: any) => {
            if (event.type === 'done_signal') { sawDone = true; return; }
            gotLive = true;
            applyStreamEvent(event as StreamEvent);
          });
          ipcUnsubRef.current = reattachUnsub;
        }

        // Poll until the run finishes (isStreaming false, or done_signal).
        // Ownership check: this loop owns the run only while streamMsgIdRef
        // still points at OUR placeholder — abort() nulls it, a NEW send()
        // stream replaces it. A follow-up typed during the reattach does NOT
        // touch it (send() just queues when streaming), so the loop survives
        // to dispatch that queue below.
        while (!cancelled && !sawDone && streamMsgIdRef.current === msgId) {
          await new Promise(r => setTimeout(r, 3000));
          try {
            if (!((await gateway.session()) as any)?.isStreaming) break;
          } catch { /* gateway hiccup — keep waiting */ }
        }
        if (reattachUnsub) {
          // abort()/send() may have already released it — only clear the ref
          // if it is still ours (removeListener itself is idempotent)
          reattachUnsub();
          if (ipcUnsubRef.current === reattachUnsub) ipcUnsubRef.current = null;
        }
        if (cancelled) {
          // Effect torn down (unmount / slug switch) while we still own the
          // stream — release the UI state so the spinner can't stick forever.
          // COMMIT FIRST (2026-07-08 scar): clearing without committing left
          // the placeholder '' on disk and the reply vanished on rehydrate.
          if (streamMsgIdRef.current === msgId && (streamTextRef.current || streamPartsRef.current.length > 0 || streamToolsRef.current.length > 0)) {
            const parts = streamTextRef.current.trim()
              ? [...streamPartsRef.current, { type: 'text' as const, content: streamTextRef.current }]
              : [...streamPartsRef.current];
            persistMessageDirect(slug, {
              id: msgId, role: 'assistant', content: streamTextRef.current,
              tools: streamToolsRef.current.length > 0 ? streamToolsRef.current : undefined,
              parts: parts.length > 0 ? parts : undefined, timestamp: Date.now(),
            } as ChatMessage);
          }
          if (streamMsgIdRef.current === msgId) {
            setStreaming(false);
            streamingRef.current = false;
            setThinking(false);
            setStreamText('');
            setStreamTools([]);
            setStreamParts([]);
            streamTextRef.current = '';
            streamToolsRef.current = [];
            streamPartsRef.current = [];
            streamMsgIdRef.current = null;
          }
          return;
        }
        if (streamMsgIdRef.current !== msgId) return;

        // Commit the finished run. Never leave a tool 'running' — it would
        // spin forever (same rule as send()'s finally).
        const truncTools = streamToolsRef.current
          .map(t => t.status === 'running' ? { ...t, status: 'done' as const, output: t.output || '[finished — output not captured]' } : t)
          .map(t => ({
            ...t,
            output: t.output && t.output.length > 4000 ? t.output.slice(0, 4000) + '\n... [truncated]' : t.output,
          }));
        streamPartsRef.current = streamPartsRef.current.map(p =>
          p.type === 'tool' && p.tool.status === 'running'
            ? { ...p, tool: { ...p.tool, status: 'done' as const, output: p.tool.output || '[finished — output not captured]' } }
            : p
        );

        let finalText = streamTextRef.current;
        if (!gotLive) {
          // No live events reached us (browser, or the run ended before the
          // resubscribe) — /chat/last has the authoritative final text.
          try {
            const data: any = await gateway.chatLast();
            if (data?.content) finalText = data.content;
          } catch { /* keep whatever auto-save had */ }
        }
        // Rendering prefers parts over content, so the final text must land in
        // parts too or it would stay hidden behind the seeded tool blocks.
        let finalParts = [...streamPartsRef.current];
        if (finalText.trim()) finalParts.push({ type: 'text', content: finalText });
        finalParts = finalParts.map(p =>
          p.type === 'tool' && p.tool.output && p.tool.output.length > 4000
            ? { ...p, tool: { ...p.tool, output: p.tool.output.slice(0, 4000) + '\n... [truncated]' } }
            : p
        );

        if (finalText || finalParts.length > 0) {
          const committed = { content: finalText, tools: truncTools.length > 0 ? truncTools : undefined, parts: finalParts.length > 0 ? finalParts : undefined, timestamp: Date.now() };
          setMessagesRef.current(prev => prev.map(m =>
            m.id === msgId ? { ...m, ...committed } : m
          ));
          // Belt-and-suspenders: land the commit on disk directly — survives a
          // project switch that suppresses the debounced setMessages write.
          persistMessageDirect(slug, { id: msgId, role: 'assistant', ...committed } as ChatMessage);
        } else {
          setMessagesRef.current(prev => prev.filter(m => m.id !== msgId));
          removeMessageDirect(slug, msgId);
        }
        setStreaming(false);
        streamingRef.current = false;
        setThinking(false);
        setStreamText('');
        setStreamTools([]);
        setStreamParts([]);
        streamTextRef.current = '';
        streamToolsRef.current = [];
        streamPartsRef.current = [];
        streamMsgIdRef.current = null;
        console.log('[chat] Reattached run finished — response committed');

        // Dispatch anything the user queued while we were reattached
        if (messageQueueRef.current.length > 0) {
          const next = messageQueueRef.current.shift()!;
          setTimeout(() => send(next.text, next.context, next.images, true), 50);
        }
        return;
      }

      // ── case 2: run already finished while we were away ──
      if (!shouldCheck) return;
      try {
        const data: any = await gateway.chatLast();
        if (!data.content || cancelled || hasSentRef.current) return;
        // If gateway has a substantially longer response, restore it
        if (!lastAssistant || data.content.length > (lastAssistant.content?.length || 0) + 50) {
          if (lastAssistant) {
            setMessages(prev => prev.map(m =>
              m.id === lastAssistant.id
                ? { ...m, content: data.content, timestamp: data.timestamp || Date.now() }
                : m
            ));
          } else if (lastMsg.role === 'user') {
            setMessages(prev => [...prev, {
              id: `msg_reconnect_${Date.now()}`,
              role: 'assistant' as const,
              content: data.content,
              timestamp: data.timestamp || Date.now(),
            }]);
          }
          console.log('[chat] Reconnected — restored response from gateway');
        }
      } catch { /* gateway unreachable */ }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, historyHydrated]);

  // Periodically flush streaming content into the messages array (every 3s)
  // This means localStorage always has the latest content — refresh can't lose it
  useEffect(() => {
    if (streaming) {
      autoSaveRef.current = setInterval(() => {
        const msgId = streamMsgIdRef.current;
        if (!msgId) return;
        const text = streamTextRef.current;
        const tools = streamToolsRef.current;
        if (!text && tools.length === 0) return;

        // Truncate tool outputs for storage
        const truncTools = tools.map(t => ({
          ...t,
          output: t.output && t.output.length > 2000
            ? t.output.slice(0, 2000) + '\n... [truncated]'
            : t.output,
        }));

        // Update the building message in-place in the messages array
        setMessagesRef.current(prev => prev.map(m =>
          m.id === msgId
            ? { ...m, content: text, tools: truncTools.length > 0 ? truncTools : undefined, timestamp: Date.now() }
            : m
        ));
      }, 3000);
    } else {
      if (autoSaveRef.current) {
        clearInterval(autoSaveRef.current);
        autoSaveRef.current = null;
      }
    }
    return () => {
      if (autoSaveRef.current) {
        clearInterval(autoSaveRef.current);
        autoSaveRef.current = null;
      }
    };
  }, [streaming]);

  // Shared stream-event reducer — feeds the live-stream refs/state. Used by
  // send()'s transport handlers AND the mount-time reattach subscription.
  const applyStreamEvent = useCallback((event: StreamEvent) => {
    switch (event.type) {
      case 'text_delta':
        if (voiceTurnRef.current && event.text) {
          window.dispatchEvent(new CustomEvent('beast:voice-delta', { detail: { text: event.text } }));
        }
        setThinking(false);
        streamTextRef.current += event.text;
        if (!streamFlushRef.current) {
          streamFlushRef.current = setTimeout(() => {
            setStreamText(streamTextRef.current);
            setStreamParts([...streamPartsRef.current]); // shared throttle — a pending thinking-part update flushes here too
            streamFlushRef.current = null;
          }, 30);
        }
        break;

      case 'thinking':
        setThinking(true);
        // Thought process is a FIRST-CLASS part, never mixed into the reply text.
        // (2026-09-04 scar: thinking text leaked into streamText and rendered as a
        // plain Beast message — Boss read internal reasoning as the answer.)
        if (event.text) {
          const parts = streamPartsRef.current;
          const last = parts[parts.length - 1];
          if (last && last.type === 'thinking') {
            streamPartsRef.current = [...parts.slice(0, -1), { type: 'thinking', content: last.content + event.text }];
          } else {
            streamPartsRef.current = [...parts, { type: 'thinking', content: event.text }];
          }
          if (!streamFlushRef.current) {
            streamFlushRef.current = setTimeout(() => {
              setStreamParts([...streamPartsRef.current]);
              setStreamText(streamTextRef.current); // shared throttle — flush both
              streamFlushRef.current = null;
            }, 30);
          }
        }
        break;

      case 'tool_start': {
        setThinking(false);
        if (streamTextRef.current.trim()) {
          streamPartsRef.current = [...streamPartsRef.current, { type: 'text', content: streamTextRef.current }];
          streamTextRef.current = '';
          setStreamText('');
        }
        const tool: ToolCall = {
          id: event.id,
          name: event.name,
          input: event.input,
          status: 'running',
        };
        streamToolsRef.current = [...streamToolsRef.current, tool];
        setStreamTools(streamToolsRef.current);
        streamPartsRef.current = [...streamPartsRef.current, { type: 'tool', tool }];
        setStreamParts([...streamPartsRef.current]);
        break;
      }

      case 'tool_end': {
        streamToolsRef.current = streamToolsRef.current.map(t =>
          t.id === event.id
            ? { ...t, output: event.output, status: event.error ? 'error' as const : 'done' as const }
            : t
        );
        setStreamTools(streamToolsRef.current);
        streamPartsRef.current = streamPartsRef.current.map(p =>
          p.type === 'tool' && p.tool.id === event.id
            ? { ...p, tool: { ...p.tool, output: event.output, status: event.error ? 'error' as const : 'done' as const } }
            : p
        );
        setStreamParts([...streamPartsRef.current]);
        break;
      }

      case 'done':
        if (event.usage) {
          setUsage(event.usage);
          window.dispatchEvent(new CustomEvent('beast:usage', { detail: event.usage }));
        }
        break;

      case 'error':
        setError(event.message);
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = useCallback(async (text: string, context?: string, images?: ChatImage[], _fromQueue?: boolean) => {
    if (!text.trim() && (!images || images.length === 0)) return;

    // If already streaming, queue the message for later
    const isFollowUp = streamingRef.current;

    setError(null);
    hasSentRef.current = true;

    // Add user message (skip if already added by queue)
    const hasImages = images && images.length > 0;
    const userMsg: ChatMessage = {
      id: `msg_${Date.now()}`,
      role: 'user',
      content: hasImages ? `${text}${text ? '\n' : ''}[${images!.length} image${images!.length > 1 ? 's' : ''} attached]` : text,
      timestamp: Date.now(),
      images: hasImages ? images : undefined,
    };

    if (isFollowUp) {
      // Queue message — it will be sent after the current stream completes.
      // Show the user message immediately so the chat feels responsive.
      setMessagesRef.current(prev => [...prev, userMsg]);
      messageQueueRef.current.push({ text: text.trim(), context, images });

      if (hasImages) {
        requestAnimationFrame(() => {
          setMessagesRef.current(prev => prev.map(m =>
            m.id === userMsg.id ? { ...m, images: undefined } : m
          ));
        });
      }
      return;
    }

    // Add a placeholder assistant message immediately — this goes into localStorage
    // so page refresh can't lose it. Content will be updated as streaming progresses.
    const assistantMsgId = `msg_${Date.now() + 1}`;
    streamMsgIdRef.current = assistantMsgId;
    const placeholderMsg: ChatMessage = {
      id: assistantMsgId,
      role: 'assistant',
      content: '',
      timestamp: Date.now(),
    };

    if (_fromQueue) {
      // User message already in chat from when it was queued — just add placeholder
      setMessagesRef.current(prev => [...prev, placeholderMsg]);
    } else {
      setMessagesRef.current(prev => [...prev, userMsg, placeholderMsg]);
    }

    // Strip base64 data after render so localStorage doesn't bloat
    if (hasImages) {
      requestAnimationFrame(() => {
        setMessagesRef.current(prev => prev.map(m =>
          m.id === userMsg.id ? { ...m, images: undefined } : m
        ));
      });
    }

    // Reset stream state
    setStreaming(true);
    streamingRef.current = true;
    setThinking(true);
    setStreamText('');
    setStreamTools([]);
    setStreamParts([]);
    streamTextRef.current = '';
    streamToolsRef.current = [];
    streamPartsRef.current = [];

    const controller = new AbortController();
    abortRef.current = controller;

    // Streaming timeout — auto-abort if agent goes silent
    // Tools can take 10+ min (npm install, python scripts, deploys)
    // After tools finish, agent may need time to analyze results — give 3 min
    let lastEventTime = Date.now();
    let hadToolsThisStream = false;
    const timeoutCheck = setInterval(() => {
      const elapsed = Date.now() - lastEventTime;
      const hasRunningTool = streamToolsRef.current.some(t => t.status === 'running');
      // No timeout during tool execution — tools finish on their own
      // 30 min for thinking/text generation — long tasks, heavy context
      if (hasRunningTool) return; // skip timeout check entirely
      const limit = 1_800_000;
      if (elapsed > limit) {
        clearInterval(timeoutCheck);
        console.log(`[chat] Stream timeout — no events for ${Math.round(elapsed/1000)}s (tool running: ${hasRunningTool}), checking /chat/last before aborting`);
        // Before giving up — check if agent actually finished and response is in history
        gateway.chatLast()
          .then((data: any) => {
            const msgId = streamMsgIdRef.current;
            if (data.content && data.content.length > 50 && msgId && !data.isStreaming) {
              // Agent finished — restore the completed response
              console.log(`[chat] Recovered completed response from /chat/last (${data.content.length} chars)`);
              setMessagesRef.current(prev => prev.map(m =>
                m.id === msgId ? { ...m, content: data.content, timestamp: data.timestamp || Date.now() } : m
              ));
              setStreaming(false);
              streamingRef.current = false;
              setThinking(false);
              setStreamText('');
              setStreamTools([]);
              setStreamParts([]);
              streamTextRef.current = '';
              streamToolsRef.current = [];
              streamPartsRef.current = [];
              streamMsgIdRef.current = null;
              controller.abort();
            } else {
              // Genuinely stuck — abort and show error
              setError('Beast stopped responding. Click retry or send a new message.');
              controller.abort();
              gateway.abort().catch(() => {});
            }
          })
          .catch(() => {
            setError('Beast stopped responding. Click retry or send a new message.');
            controller.abort();
            gateway.abort().catch(() => {});
          });
      }
    }, 10_000);

    // --- Shared event handler (used by both IPC and SSE paths) ---
    const handleStreamEvent = (event: StreamEvent) => {
      lastEventTime = Date.now();
      if (event.type === 'error') setLastFailedMessage(text);
      applyStreamEvent(event);
    };

    try {
      const messageText = text.trim() || (images?.length ? 'What do you see in this image?' : '');
      const payload: Record<string, unknown> = { message: messageText };
      if (context) payload.context = context;
      if (images && images.length > 0) payload.images = images;

      if (isElectron) {
        // --- Electron IPC path ---
        // Clean up any leaked listener from a previous stream (timeout recovery, error, etc.)
        if (ipcUnsubRef.current) {
          ipcUnsubRef.current();
          ipcUnsubRef.current = null;
        }
        // Subscribe to streaming events before sending
        const unsubscribe = chatApi.onChatEvent((event: any) => {
          if (event.type === 'done_signal') {
            // Agent finished — unsubscribe and let finally block handle cleanup
            unsubscribe();
            ipcUnsubRef.current = null;
            controller.abort(); // Triggers finally block
            return;
          }
          handleStreamEvent(event as StreamEvent);
        });
        ipcUnsubRef.current = unsubscribe;

        // Send message via IPC (returns when agent finishes)
        try {
          await window.beast!.invoke('chat:send', payload);
        } catch (err: unknown) {
          unsubscribe();
          ipcUnsubRef.current = null;
          throw err;
        }
      } else {
        // --- Browser SSE path (existing behavior) ---
        const res = await fetch(`${GATEWAY_URL}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });

        if (!res.ok) {
          throw new Error(`Gateway error: ${res.status}`);
        }

        const reader = res.body?.getReader();
        if (!reader) throw new Error('No response stream');

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const data = line.slice(6);
            if (data === '[DONE]') continue;

            try {
              handleStreamEvent(JSON.parse(data));
            } catch {
              // Skip malformed events
            }
          }
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        // User aborted — that's fine
      } else {
        const errMsg = err instanceof Error ? err.message : 'Connection failed';
        setError(errMsg);
        setLastFailedMessage(text);
        setConnectionStatus('disconnected');
      }
    } finally {
      clearInterval(timeoutCheck);

      // Clear any pending throttled text update
      if (streamFlushRef.current) {
        clearTimeout(streamFlushRef.current);
        streamFlushRef.current = null;
      }

      // Final commit — update the placeholder message with final content.
      // A finished stream must never commit a tool still in 'running' — if a
      // tool_end event was dropped, that block spins forever (2026-07-03 scar).
      const finalText = streamTextRef.current;
      const finalTools = streamToolsRef.current.map(t =>
        t.status === 'running'
          ? { ...t, status: 'done' as const, output: t.output || '[finished — output not captured]' }
          : t
      );
      streamPartsRef.current = streamPartsRef.current.map(p =>
        p.type === 'tool' && p.tool.status === 'running'
          ? { ...p, tool: { ...p.tool, status: 'done' as const, output: p.tool.output || '[finished — output not captured]' } }
          : p
      );
      const msgId = streamMsgIdRef.current;

      if (msgId) {
        if (finalText || finalTools.length > 0 || streamPartsRef.current.length > 0) {
          // Truncate large tool outputs for localStorage
          const truncatedTools = finalTools.length > 0
            ? finalTools.map(t => ({
                ...t,
                output: t.output && t.output.length > 4000
                  ? t.output.slice(0, 4000) + '\n... [truncated for storage]'
                  : t.output,
              }))
            : undefined;

          // Build final parts — add any trailing text
          let finalParts = [...streamPartsRef.current];
          if (finalText.trim()) {
            finalParts.push({ type: 'text', content: finalText });
          }
          // Truncate tool outputs in parts too
          finalParts = finalParts.map(p =>
            p.type === 'tool' && p.tool.output && p.tool.output.length > 4000
              ? { ...p, tool: { ...p.tool, output: p.tool.output.slice(0, 4000) + '\n... [truncated]' } }
              : p
          );

          // Update the placeholder with final content
          setMessagesRef.current(prev => prev.map(m =>
            m.id === msgId
              ? { ...m, content: finalText, tools: truncatedTools, parts: finalParts.length > 0 ? finalParts : undefined, timestamp: Date.now() }
              : m
          ));
        } else {
          // No content at all — remove empty placeholder, error shows in error bar if applicable
          setMessagesRef.current(prev => prev.filter(m => m.id !== msgId));
        }
      }

      // Clean up IPC listener if still active (belt-and-suspenders)
      if (ipcUnsubRef.current) {
        ipcUnsubRef.current();
        ipcUnsubRef.current = null;
      }

      // Clear stream state
      setStreaming(false);
      streamingRef.current = false;
      setThinking(false);
      setStreamText('');
      setStreamTools([]);
      setStreamParts([]);
      streamTextRef.current = '';
      streamToolsRef.current = [];
      streamPartsRef.current = [];
      streamMsgIdRef.current = null;
      abortRef.current = null;

      // {{DO:}} tokens in a TYPED turn's reply execute here (2026-07-21 scar: Boss typed "Open
      // TORE session and pop it out in the focus mode", Fable emitted the token, nothing ran —
      // the executor only lived in BeastMode's voice-delta path). Voice turns already executed
      // their tokens mid-stream in BeastMode; running them again would double-fire, so skip those.
      if (!voiceTurnRef.current && finalText && finalText.includes('{{DO:')) {
        try { executeDoTokens(finalText); } catch { /* command layer not mounted on this page */ }
      }

      // voice bridge: tell BeastMode the turn is over so TTS can drain
      if (voiceTurnRef.current) {
        voiceTurnRef.current = false;
        window.dispatchEvent(new CustomEvent('beast:voice-done'));
      }

      // Process queued messages (sent while this stream was active)
      if (messageQueueRef.current.length > 0) {
        const next = messageQueueRef.current.shift()!;
        // Small delay to let React commit the state reset before starting new stream
        // _fromQueue=true skips adding user message (already shown when queued)
        setTimeout(() => send(next.text, next.context, next.images, true), 50);
      }
    }
  }, [streaming]);

  // voice bridge receiver — BeastMode transcripts enter the normal pipeline
  useEffect(() => {
    const onVoiceSend = (e: Event) => {
      const { text, context } = (e as CustomEvent).detail || {};
      if (!text) return;
      // VOICE ROUTING (Boss 2026-07-11): this is the MAIN (__beast) chat. Only take the
      // turn when Beast is the on-screen session — otherwise a non-main session handles it.
      const target = (window as any).__beastVoiceTarget;
      if (target && target !== '__beast') return;
      // SINGLE-FLIGHT (Boss 2026-07-19 double/triple-send fix): if two chat surfaces of the same
      // target are mounted, only the FIRST to see a given turn may consume it.
      const g = (window as any); const vid = (e as CustomEvent).detail?.vid || `t:${text}`;
      g.__beastVoiceSeen = g.__beastVoiceSeen || new Map();
      const nowT = Date.now(); const seenT = g.__beastVoiceSeen.get(vid);
      if (seenT && nowT - seenT < 5000) return; g.__beastVoiceSeen.set(vid, nowT);
      voiceTurnRef.current = true;
      window.dispatchEvent(new CustomEvent('beast:voice-send-ack'));
      send(`🎙 ${text}`, context);
    };
    window.addEventListener('beast:voice-send', onVoiceSend);
    return () => window.removeEventListener('beast:voice-send', onVoiceSend);
  }, [send]);

  const abort = useCallback(() => {
    // Cancel the client-side fetch (stops reading SSE stream)
    abortRef.current?.abort();
    // Tell the gateway to stop the agent (explicit user action, not a disconnect)
    gateway.abort().catch(() => {});

    // Clean up IPC listener immediately — don't leave it dangling
    if (ipcUnsubRef.current) {
      ipcUnsubRef.current();
      ipcUnsubRef.current = null;
    }

    // Commit whatever content we have so far into the placeholder message
    const msgId = streamMsgIdRef.current;
    if (msgId) {
      const currentText = streamTextRef.current;
      const currentTools = streamToolsRef.current;
      let currentParts = [...streamPartsRef.current];
      if (currentText.trim()) {
        currentParts.push({ type: 'text' as const, content: currentText });
      }
      if (currentText || currentTools.length > 0 || currentParts.length > 0) {
        setMessagesRef.current(prev => prev.map(m =>
          m.id === msgId
            ? { ...m, content: currentText, tools: currentTools.length > 0 ? currentTools : undefined, parts: currentParts.length > 0 ? currentParts : undefined, timestamp: Date.now() }
            : m
        ));
      } else {
        // No content — remove the empty placeholder
        setMessagesRef.current(prev => prev.filter(m => m.id !== msgId));
      }
    }

    // Clean up any stuck running tools in saved messages
    setMessagesRef.current(prev => {
      let changed = false;
      const fixed = prev.map(m => {
        const fixTools = m.tools?.map(t => {
          if (t.status === 'running') { changed = true; return { ...t, status: 'error' as const, output: t.output || '[stopped]' }; }
          return t;
        });
        const fixParts = m.parts?.map(p => {
          if (p.type === 'tool' && p.tool.status === 'running') { changed = true; return { ...p, tool: { ...p.tool, status: 'error' as const, output: p.tool.output || '[stopped]' } }; }
          return p;
        });
        return changed ? { ...m, tools: fixTools, parts: fixParts } : m;
      });
      return changed ? fixed : prev;
    });

    // Clear message queue — user explicitly stopped, don't auto-send queued messages
    messageQueueRef.current = [];

    // Fully reset streaming state so next send() starts fresh
    setStreaming(false);
    streamingRef.current = false;
    setThinking(false);
    setStreamText('');
    setStreamTools([]);
    setStreamParts([]);
    streamTextRef.current = '';
    streamToolsRef.current = [];
    streamPartsRef.current = [];
    streamMsgIdRef.current = null;
    abortRef.current = null;
    if (streamFlushRef.current) {
      clearTimeout(streamFlushRef.current);
      streamFlushRef.current = null;
    }
  }, []);

  const retry = useCallback(() => {
    if (!lastFailedMessage) return;
    const msg = lastFailedMessage;
    setLastFailedMessage(null);
    setError(null);
    setMessagesRef.current(prev => {
      const last = prev[prev.length - 1];
      if (last?.role === 'user' && last.content === msg) {
        return prev.slice(0, -1);
      }
      return prev;
    });
    send(msg);
  }, [lastFailedMessage, send]);

  const clear = useCallback(() => {
    messageQueueRef.current = [];
    setMessagesRef.current([]);
    setUsage(null);
    setError(null);
    setLastFailedMessage(null);
    gateway.sessionNew().catch(() => {});
  }, []);

  // Delete a single message so no future session is aware of it. Disk is the
  // record Beast replays from, so we scrub disk FIRST (authoritative) — the
  // 4s merge-poll and the merge-on-write both re-add anything that's on disk
  // but not in local state, so removing locally first would just resurrect it.
  // Returns the removed message + its index so the caller can offer undo.
  const deleteMessage = useCallback(async (id: string): Promise<{ msg: ChatMessage; idx: number } | null> => {
    let removed: { msg: ChatMessage; idx: number } | null = null;
    const snapshot = messagesRef.current;
    const idx = snapshot.findIndex(m => m.id === id);
    if (idx >= 0) removed = { msg: snapshot[idx], idx };
    await removeMessageDirect(slug, id);            // scrub disk first
    setMessagesRef.current(prev => prev.filter(m => m.id !== id));
    // The UI store above is only what's RENDERED. The model's memory is the
    // SDK context tree — scrub it there too or the AI still remembers a
    // message that vanished from the screen. This is the Beast main chat,
    // which runs in Electron's own session.
    if (removed) {
      gateway.forgetMessage(
        slug,
        { role: removed.msg.role, text: textPartsOf(removed.msg), timestamp: removed.msg.timestamp },
        { electronMain: true, main: true },
      ).catch(() => {});
    }
    return removed;
  }, [slug]);

  // Undo a delete — re-insert at the original position. The debounced
  // merge-write persists the full array back to disk in timestamp order, so
  // disk and view stay consistent (and the merge-poll won't remove it).
  const restoreMessage = useCallback((msg: ChatMessage, idx: number) => {
    setMessagesRef.current(prev => {
      if (prev.some(m => m.id === msg.id)) return prev;
      const next = [...prev];
      next.splice(Math.min(Math.max(idx, 0), next.length), 0, msg);
      return next;
    });
  }, []);

  return {
    messages,
    setMessages,
    streaming,
    streamText,
    streamTools,
    streamParts,
    thinking,
    usage,
    error,
    loaded,
    historyHydrated,
    connectionStatus,
    lastFailedMessage,
    activeProject,
    send,
    abort,
    retry,
    clear,
    deleteMessage,
    restoreMessage,
  };
}
